import type { ReactElement } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/lib/utils';

interface MarkdownMessageProps {
  text: string;
  className?: string;
}

export function MarkdownMessage({ text, className }: MarkdownMessageProps): ReactElement {
  return (
    <div
      className={cn(
        'text-foreground text-[14px] leading-[1.65]',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="mt-3.5 mb-1.5 text-[18px] leading-[1.35] font-semibold">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-3 mb-1 text-[16px] leading-[1.35] font-semibold">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-2.5 mb-1 text-[15px] leading-[1.4] font-semibold">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-2 mb-0.5 text-[14px] leading-[1.4] font-semibold">{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className="mt-2 mb-0.5 text-[14px] leading-[1.4] font-semibold">{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className="mt-2 mb-0.5 text-[13.5px] leading-[1.4] font-semibold">{children}</h6>
  ),
  p: ({ children }) => <p className="my-1.5 text-[14px] leading-[1.65]">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => {
    if (!href || !isSafeHref(href)) {
      return <span>{children}</span>;
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline"
      >
        {children}
      </a>
    );
  },
  ul: ({ children }) => <ul className="my-1.5 list-disc pl-6">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 list-decimal pl-6">{children}</ol>,
  li: ({ children }) => <li className="mb-0.5 text-[14px] leading-[1.6] [&>p]:my-0">{children}</li>,
  code({ className: cls, children, ...rest }) {
    const isBlock = (cls ?? '').includes('language-');
    if (isBlock) {
      return (
        <code className="block font-mono text-[12.5px]" {...rest}>
          {children}
        </code>
      );
    }
    return (
      <code className="bg-muted rounded px-1.5 py-0.5 font-mono text-[12.5px]" {...rest}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-code-bg my-2 overflow-x-auto rounded p-3 text-[12.5px] leading-[1.5]">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-border text-muted-foreground my-2 border-l-[3px] pl-3 [&>p]:my-1">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="border-border my-3 border-t" />,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="min-w-full border-collapse text-[14px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-code-bg">{children}</thead>,
  th: ({ children }) => (
    <th className="border-border border-b px-3 py-1.5 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-border border-b px-3 py-1.5 align-top">{children}</td>
  ),
};

const SAFE_HREF_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function isSafeHref(href: string): boolean {
  if (/^[a-z][a-z0-9+.-]*:/i.exec(href) === null) return true;
  try {
    const url = new URL(href, 'http://_local_');
    return SAFE_HREF_SCHEMES.has(url.protocol);
  } catch {
    return false;
  }
}
